import { ViewLayoutPlacement } from "@faire/web-api/indigofair/data/ViewLayoutPlacement";
import * as React from "react";
import { useLayoutEffect } from "react";

import { SDUIRenderer } from "@faire/retailer/components/SDUI/SDUIRenderer/SDUIRenderer";
import {
  ComponentRenderMap,
  SectionRenderMap,
} from "@faire/retailer/components/SDUI/types";
import { calculateNumUPLTComponentsPerClientSize } from "@faire/retailer/components/SDUI/UPLT/calculateNumComponentsPerClientSize";
import { MaybeSDUIUPLTWrapper } from "@faire/retailer/components/SDUI/UPLT/MaybeSDUIUPLTWrapper";
import { PageInitialized } from "@faire/retailer/events";
import { useBrowseV3LayoutQuery } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/v3/useBrowseV3LayoutQuery";
import { useSDUICategoryPageUPLT } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/useSDUICategoryPageUPLT";
import { useSDUIHubPageUPLT } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/useSDUIHubPageUPLT";
import { useIsHubCategory } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/useIsHubCategory";
import { useIsSDUISeasonalHubPage } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/useIsSDUISeasonalHubPage";
import { LoggedInCategoryPageLoadingSkeleton } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/CategoryPageLoadingSkeleton/LoggedInCategoryPageLoadingSkeleton";

export const SDUICategoryPageContentsV2: React.FC<{
  componentRenderers: ComponentRenderMap;
  sectionRenderers: SectionRenderMap;
}> = ({ componentRenderers, sectionRenderers }) => {
  const { data: topViewLayout, isFetching: isTopLayoutLoading } =
    useBrowseV3LayoutQuery(ViewLayoutPlacement.TOP);

  const isSeasonalHubCategory = useIsSDUISeasonalHubPage();

  const { data: fpsViewLayout, isFetching: isFPSLayoutLoading } =
    useBrowseV3LayoutQuery(ViewLayoutPlacement.FPS);

  const { isHubCategory } = useIsHubCategory();

  const hubPageLoadTimingProps = useSDUIHubPageUPLT();
  const categoryPageLoadTimingProps = useSDUICategoryPageUPLT();
  const loadTimingProps = isSeasonalHubCategory
    ? hubPageLoadTimingProps
    : categoryPageLoadTimingProps;

  useLayoutEffect(() => {
    if (isHubCategory && !isTopLayoutLoading) {
      PageInitialized.publish();
    } else if (!isHubCategory && !isFPSLayoutLoading) {
      PageInitialized.publish();
    }
  }, [isHubCategory, isTopLayoutLoading, isFPSLayoutLoading]);

  const numUPLTComponentsPerClientSize = React.useMemo(() => {
    if (isHubCategory && topViewLayout) {
      return calculateNumUPLTComponentsPerClientSize(topViewLayout);
    }

    if (!isHubCategory && fpsViewLayout) {
      return calculateNumUPLTComponentsPerClientSize(fpsViewLayout);
    }

    return undefined;
  }, [isHubCategory, topViewLayout, fpsViewLayout]);

  return (
    <>
      <MaybeSDUIUPLTWrapper
        {...loadTimingProps}
        numComponentsPerClientSize={numUPLTComponentsPerClientSize}
        shouldTrackUPLT={isHubCategory}
      >
        {isTopLayoutLoading || topViewLayout === undefined ? null : (
          <SDUIRenderer
            viewLayout={topViewLayout}
            componentRenderers={componentRenderers}
            sectionRenderers={sectionRenderers}
            UPLTprops={{
              shouldRenderBelowTheFold: false,
            }}
          />
        )}
      </MaybeSDUIUPLTWrapper>
      <MaybeSDUIUPLTWrapper
        {...loadTimingProps}
        numComponentsPerClientSize={numUPLTComponentsPerClientSize}
        shouldTrackUPLT={!isHubCategory}
      >
        {isFPSLayoutLoading || fpsViewLayout === undefined ? (
          <LoggedInCategoryPageLoadingSkeleton
            isFullWidthCategoryPage={!isSeasonalHubCategory}
          />
        ) : (
          <SDUIRenderer
            viewLayout={fpsViewLayout}
            componentRenderers={componentRenderers}
            sectionRenderers={sectionRenderers}
            UPLTprops={{
              shouldRenderBelowTheFold: false,
            }}
          />
        )}
      </MaybeSDUIUPLTWrapper>
    </>
  );
};
